//mushroom-workflow-page.tsx

import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Divider,
} from "@mui/material";
import Page from "../components/page";

type MushroomWorkflowStage =
  | "agar"
  | "liquid_culture"
  | "grain_spawn"
  | "bulk_spawn"
  | "fruiting_block"
  | "casing"
  | "fruiting"
  | "harvest";

interface WorkflowDefaults {
  enabled: Record<MushroomWorkflowStage, boolean>;
  days: Record<MushroomWorkflowStage, number>;
}

const workflowStorageKey = "mushroomWorkflowDefaults";

const stageLabel = (s: MushroomWorkflowStage) => {
  switch (s) {
    case "agar":
      return "Agar / Plate";
    case "liquid_culture":
      return "Liquid Culture";
    case "grain_spawn":
      return "Grain Spawn";
    case "bulk_spawn":
      return "Bulk Spawn";
    case "fruiting_block":
      return "Fruiting Block (Colonization)";
    case "casing":
      return "Casing";
    case "fruiting":
      return "Fruiting (to first harvest)";
    case "harvest":
      return "Harvest Window (days)";
    default:
      return s;
  }
};

const defaultWorkflow: WorkflowDefaults = {
  enabled: {
    agar: true,
    liquid_culture: true,
    grain_spawn: true,
    bulk_spawn: false,
    fruiting_block: true,
    casing: false,
    fruiting: true,
    harvest: true,
  },
  days: {
    agar: 5,
    liquid_culture: 10,
    grain_spawn: 14,
    bulk_spawn: 10,
    fruiting_block: 14,
    casing: 2,
    fruiting: 7,
    harvest: 5,
  },
};

export default function MushroomWorkflowPage() {
  const [workflow, setWorkflow] = useState<WorkflowDefaults>(defaultWorkflow);

  useEffect(() => {
    const raw = window.localStorage.getItem(workflowStorageKey);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw) as WorkflowDefaults;

      if (parsed?.enabled && parsed?.days) {
        setWorkflow({
          enabled: { ...defaultWorkflow.enabled, ...parsed.enabled },
          days: { ...defaultWorkflow.days, ...parsed.days },
        });
      }
    } catch {
      window.localStorage.removeItem(workflowStorageKey);
    }
  }, []);

  const saveWorkflow = (next: WorkflowDefaults) => {
    setWorkflow(next);
    window.localStorage.setItem(workflowStorageKey, JSON.stringify(next));
  };

  const handleToggleEnabled = (
    stage: MushroomWorkflowStage,
    checked: boolean,
  ) => {
    saveWorkflow({
      ...workflow,
      enabled: {
        ...workflow.enabled,
        [stage]: checked,
      },
    });
  };

  const handleDaysChange = (stage: MushroomWorkflowStage, value: string) => {
    const parsed = Number(value);

    saveWorkflow({
      ...workflow,
      days: {
        ...workflow.days,
        [stage]: Number.isFinite(parsed) ? parsed : 0,
      },
    });
  };

  const stages: MushroomWorkflowStage[] = [
    "agar",
    "liquid_culture",
    "grain_spawn",
    "bulk_spawn",
    "fruiting_block",
    "casing",
    "fruiting",
    "harvest",
  ];

  return (
    <Page title="Workflow">
      <Box sx={{ maxWidth: 650 }}>
        <Typography sx={{ mb: 2 }}>
          Enable the stages your farm uses, and set default timing (in days).
        </Typography>

        {stages.map((stage, idx) => (
          <React.Fragment key={stage}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                py: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={workflow.enabled[stage]}
                    onChange={(e) =>
                      handleToggleEnabled(stage, e.target.checked)
                    }
                  />
                }
                label={stageLabel(stage)}
              />

              <TextField
                label="Days"
                value={workflow.days[stage]}
                onChange={(e) => handleDaysChange(stage, e.target.value)}
                size="small"
                sx={{ width: 140 }}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              />
            </Box>

            {idx !== stages.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Box>
    </Page>
  );
}
